import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import SparklingGold from './SparklingGold';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    age: '',
    instagram: '',
    tiktok: '',
    betreuung: 'content',
    activity: '',
    description: '',
    goals: '',
    phone: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!ageConfirmed) {
      toast.error('Bitte bestätige, dass du mindestens 18 Jahre alt bist.');
      return;
    }

    // Validierung basierend auf Betreuungsart
    if (formData.betreuung === 'content') {
      if (!formData.instagram || formData.instagram.trim() === '') {
        toast.error('Bitte gib deinen Instagram-Username an.');
        return;
      }
    } else if (formData.betreuung === 'tiktok-live') {
      if (!formData.tiktok || formData.tiktok.trim() === '') {
        toast.error('Bitte gib deinen TikTok-Username an.');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/applications/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          age: formData.age ? parseInt(formData.age) : null,
          instagram: formData.instagram || null,
          tiktok: formData.tiktok || null,
          betreuung: formData.betreuung,
          activity: formData.activity || null,
          description: formData.description || null,
          goals: formData.goals || null,
          phone: formData.phone || null
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && data.success) {
        // Redirect to success page (GA4 event triggered by Custom Rule in GA4 Interface)
        window.location.href = '/bewerbung-erfolgreich.html';
      } else {
        toast.error('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      
      if (error.message?.includes('HTTP error')) {
        toast.error('Serverfehler. Bitte versuche es später erneut.');
      } else {
        toast.error('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bestimme, welche Felder erforderlich sind
  const isInstagramRequired = formData.betreuung === 'content';
  const isTikTokRequired = formData.betreuung === 'tiktok-live';

  return (
    <section id="contact-form" className="py-24 lg:py-32 bg-gradient-to-b from-black via-gray-900/50 to-black relative overflow-hidden">
      {/* Sparkling Gold Effects */}
      <SparklingGold density="low" />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Title */}
        <div className="text-center mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Als Creator bei Bad Girl's Society bewerben
          </h2>
          <div className="w-20 h-1 bg-accent-red mx-auto mb-8"></div>
          <p className="text-white/70 text-lg lg:text-xl max-w-3xl mx-auto">
            Füll das Formular aus und wir melden uns zeitnah für ein erstes Gespräch. Du kannst dich für Content & Marketing oder TikTok LIVE Management bewerben.
          </p>
        </div>

        {/* Google Maps Standort */}
        <div className="max-w-4xl mx-auto mb-16 lg:mb-20">
          <div className="bg-gradient-to-br from-gray-900/60 to-black/60 backdrop-blur-sm border border-gold/10 rounded-2xl p-6 lg:p-8">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <svg className="text-gold" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              Unser Standort in Köln
            </h3>
            <p className="text-white/70 mb-6">
              Bad Girl's Society<br/>
              Auf d. Driesch 19, 51107 Köln
            </p>
            <div className="rounded-xl overflow-hidden">
              <iframe
                src="https://maps.google.com/maps?q=50.9328582,7.0422701&hl=de&z=16&output=embed"
                width="100%"
                height="360"
                style={{border: 0}}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Standort Bad Girl's Society, Auf d. Driesch 19, 51107 Köln"
              ></iframe>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-gradient-to-br from-gray-900/60 to-black/60 backdrop-blur-sm border border-gold/10 rounded-2xl p-8 lg:p-12">
              
              {/* Name */}
              <div className="mb-6">
                <Label htmlFor="name" className="text-white text-base md:text-lg mb-3 block">
                  Name <span className="text-accent-red">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Dein vollständiger Name"
                  className="bg-black/50 border-gold/30 text-white placeholder:text-white/40 focus:border-gold h-12 md:h-14 text-base"
                />
              </div>

              {/* Email */}
              <div className="mb-6">
                <Label htmlFor="email" className="text-white text-base md:text-lg mb-3 block">
                  E-Mail <span className="text-accent-red">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="deine@email.de"
                  className="bg-black/50 border-gold/30 text-white placeholder:text-white/40 focus:border-gold h-12 md:h-14 text-base"
                />
              </div>

              {/* Age Confirmation */}
              <div className="mb-6">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="age-confirm"
                    checked={ageConfirmed}
                    onChange={(e) => setAgeConfirmed(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-gold/30 bg-black/50 text-gold focus:ring-gold"
                  />
                  <Label htmlFor="age-confirm" className="text-white text-base cursor-pointer">
                    Ich bestätige, dass ich mindestens 18 Jahre alt bin <span className="text-accent-red">*</span>
                  </Label>
                </div>
              </div>

              {/* Betreuung Selection - FRÜHER IM FORMULAR */}
              <div className="mb-6">
                <Label className="text-white text-base md:text-lg mb-4 block">
                  Gewünschte Betreuung <span className="text-accent-red">*</span>
                </Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-4 bg-black/30 rounded-lg border border-gold/20 hover:border-gold/40 transition-colors cursor-pointer">
                    <input
                      type="radio"
                      id="content"
                      name="betreuung"
                      value="content"
                      checked={formData.betreuung === 'content'}
                      onChange={handleChange}
                      className="w-5 h-5 text-gold focus:ring-gold"
                    />
                    <Label htmlFor="content" className="text-white text-base cursor-pointer flex-1">
                      Content & Marketing
                    </Label>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-black/30 rounded-lg border border-accent-red/20 hover:border-accent-red/40 transition-colors cursor-pointer">
                    <input
                      type="radio"
                      id="tiktok-live"
                      name="betreuung"
                      value="tiktok-live"
                      checked={formData.betreuung === 'tiktok-live'}
                      onChange={handleChange}
                      className="w-5 h-5 text-accent-red focus:ring-accent-red"
                    />
                    <Label htmlFor="tiktok-live" className="text-white text-base cursor-pointer flex-1">
                      TikTok LIVE Management
                    </Label>
                  </div>
                </div>
              </div>

              {/* Instagram - abhängig von Betreuung */}
              <div className="mb-6">
                <Label htmlFor="instagram" className="text-white text-base md:text-lg mb-3 block">
                  Instagram Username {isInstagramRequired && <span className="text-accent-red">*</span>}
                  {!isInstagramRequired && <span className="text-white/50 text-sm ml-2">(optional)</span>}
                </Label>
                <Input
                  id="instagram"
                  name="instagram"
                  type="text"
                  value={formData.instagram}
                  onChange={handleChange}
                  required={isInstagramRequired}
                  placeholder="@dein_username"
                  className="bg-black/50 border-gold/30 text-white placeholder:text-white/40 focus:border-gold h-12 md:h-14 text-base"
                />
              </div>

              {/* TikTok - abhängig von Betreuung */}
              <div className="mb-6">
                <Label htmlFor="tiktok" className="text-white text-base md:text-lg mb-3 block">
                  TikTok Username {isTikTokRequired && <span className="text-accent-red">*</span>}
                  {!isTikTokRequired && <span className="text-white/50 text-sm ml-2">(optional)</span>}
                </Label>
                <Input
                  id="tiktok"
                  name="tiktok"
                  type="text"
                  value={formData.tiktok}
                  onChange={handleChange}
                  required={isTikTokRequired}
                  placeholder="@dein_username"
                  className="bg-black/50 border-gold/30 text-white placeholder:text-white/40 focus:border-gold h-12 md:h-14 text-base"
                />
              </div>

              {/* Activity */}
              <div className="mb-6">
                <Label htmlFor="activity" className="text-white text-base md:text-lg mb-3 block">
                  Aktuelle Social-Media-Aktivität
                </Label>
                <Textarea
                  id="activity"
                  name="activity"
                  value={formData.activity}
                  onChange={handleChange}
                  placeholder="Beschreibe kurz deine aktuellen Aktivitäten auf Social Media..."
                  rows={3}
                  className="bg-black/50 border-gold/30 text-white placeholder:text-white/40 focus:border-gold text-base resize-none"
                />
              </div>

              {/* Description */}
              <div className="mb-6">
                <Label htmlFor="description" className="text-white text-base md:text-lg mb-3 block">
                  Kurze Beschreibung über dich
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Erzähl uns etwas über dich als Creator..."
                  rows={4}
                  className="bg-black/50 border-gold/30 text-white placeholder:text-white/40 focus:border-gold text-base resize-none"
                />
              </div>

              {/* Goals */}
              <div className="mb-6">
                <Label htmlFor="goals" className="text-white text-base md:text-lg mb-3 block">
                  Deine Ziele
                </Label>
                <Textarea
                  id="goals"
                  name="goals"
                  value={formData.goals}
                  onChange={handleChange}
                  placeholder="Was möchtest du erreichen? Welche Ziele verfolgst du?"
                  rows={4}
                  className="bg-black/50 border-gold/30 text-white placeholder:text-white/40 focus:border-gold text-base resize-none"
                />
              </div>

              {/* Phone (optional) */}
              <div className="mb-8">
                <Label htmlFor="phone" className="text-white text-base md:text-lg mb-3 block">
                  Telefonnummer (optional)
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+49 ..."
                  className="bg-black/50 border-gold/30 text-white placeholder:text-white/40 focus:border-gold h-12 md:h-14 text-base"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting || isSubmitted}
                className="w-full bg-accent-red hover:bg-accent-red/90 text-white py-6 md:py-7 rounded-full text-base md:text-lg uppercase tracking-wider font-semibold transition-all hover:scale-105 shadow-lg shadow-accent-red/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitted ? (
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircle size={24} />
                    Erfolgreich gesendet!
                  </span>
                ) : isSubmitting ? (
                  'Wird gesendet...'
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send size={20} />
                    Bewerbung absenden
                  </span>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
